import {
  Check,
  CheckCircle2,
  Circle,
  Copy,
  Link2,
  Loader2,
  XCircle,
} from 'lucide-react';
import { GmailIcon } from '@/icons/gmail-icon';
import {
  AirtableIcon,
  GitHubIcon,
  GoogleCalendarIcon,
  GoogleDocsIcon,
  GoogleDriveIcon,
  GoogleSheetsIcon,
  HubSpotIcon,
  JiraIcon,
  LinearIcon,
  NotionIcon,
  OutlookIcon,
  SlackIcon,
  SupabaseIcon,
  XIcon,
} from '@/icons/service-icons';
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  createContext,
} from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MermaidDiagram } from '@/components/mermaid-diagram';
import { IntegrationConnectCard } from '@/components/integration-connect-card';
import { CarbonArcConnectCard } from '@/components/carbon-arc-connect-card';
import { useWorkflowCardOpen } from '@/components/agent-chat/chat-workflow-card';
import { WorkflowCard } from '@/components/workflow/workflow-card';
import { useWorkflowSummary } from '@/hooks/queries/workflow/use-workflow-summary';
import { isNavigableHref } from '@/utils/url-utils';
import { toSameOriginWorkflowUrl } from '@/utils/workflow-file-url';

type MarkdownRendererProps = {
  content: string | null | undefined;
  className?: string;
  // Forwarded to embedded MermaidDiagram blocks via context so they can
  // defer rendering until the surrounding chat message stops streaming.
  // Optional with a `false` default so existing non-streaming callers
  // (release notes, websocket status panel, etc.) are unaffected.
  isStreaming?: boolean;
};

// Context plumbing: MarkdownRenderer → CodeBlock → MermaidDiagram.
// react-markdown's `components` map only forwards element-level props to
// each renderer, so we use context to share the streaming flag without
// re-wrapping CodeBlock on every render (which would defeat reconciliation
// and re-mount MermaidDiagram on every chunk).
const MarkdownStreamingContext = createContext<boolean>(false);

// A fenced code block is `pre > code`; an inline backtick span is a bare
// `code` with no `pre` ancestor -- but a span can itself contain a raw
// newline (CommonMark only collapses those inside HTML, not inside a code
// span), so `code`'s own text content can't tell the two apart. The `pre`
// renderer marks real ancestry instead of guessing from content.
const CodeFenceContext = createContext(false);

type ToolCallInfo = {
  name: string;
  detail: string;
  status: 'running' | 'completed' | 'failed' | 'unknown';
  workflowLink?: { workflowId: string; text: string };
};

/**
 * Extracts tool-call info from blockquote children.
 * Tool-call blockquotes are rendered as: > **ToolName** detail...
 * Returns null if the blockquote is not a tool-call pattern.
 */
function extractToolCallInfo(children: React.ReactNode): ToolCallInfo | null {
  const childArray = React.Children.toArray(children);
  if (childArray.length !== 1) return null;

  const child = childArray[0];
  if (!React.isValidElement(child) || child.type !== 'p') return null;

  const pProps = child.props as { children?: React.ReactNode };
  const pChildren = React.Children.toArray(pProps.children);
  if (pChildren.length === 0) return null;

  // First child must be a <strong> element (the tool name)
  const first = pChildren[0];
  if (!React.isValidElement(first) || first.type !== 'strong') return null;

  const strongProps = first.props as { children?: React.ReactNode };
  const name = getTextContent(strongProps.children).trim();
  if (!name) return null;

  // Remaining children form the detail text; also capture workflow link
  let workflowLink: ToolCallInfo['workflowLink'] = undefined;
  const restText = pChildren
    .slice(1)
    .map((c) => {
      if (typeof c === 'string') return c;
      if (React.isValidElement(c) && c.type === 'a') {
        const aProps = c.props as {
          href?: string;
          children?: React.ReactNode;
        };
        const linkedWorkflowId = aProps.href
          ? workflowIdFromHref(aProps.href)
          : null;
        if (linkedWorkflowId) {
          workflowLink = {
            workflowId: linkedWorkflowId,
            text: getTextContent(aProps.children).trim(),
          };
        }
        return '';
      }
      return '';
    })
    .join('')
    .trim();

  // Determine status from the detail text
  let status: ToolCallInfo['status'] = 'unknown';
  if (restText.endsWith('...') || restText.endsWith('…')) {
    status = 'running';
  } else if (/completed/i.test(restText)) {
    status = 'completed';
  } else if (/failed|error/i.test(restText)) {
    status = 'failed';
  }

  // Clean up detail text (remove leading punctuation like ": " or " — ")
  const detail = restText.replace(/^[\s:—–\-·]+/, '').trim();

  return { name, detail, status, workflowLink };
}

/** Known connection link patterns → icon + label */
const CONNECTION_PATTERNS: {
  pattern: RegExp;
  icon: React.FC<{ className?: string; size?: number }>;
  label: string;
}[] = [
  { pattern: /gmail/i, icon: GmailIcon, label: 'Connect Gmail' },
  { pattern: /outlook/i, icon: OutlookIcon, label: 'Connect Outlook' },
  { pattern: /slack/i, icon: SlackIcon, label: 'Connect Slack' },
  { pattern: /github/i, icon: GitHubIcon, label: 'Connect GitHub' },
  { pattern: /jira/i, icon: JiraIcon, label: 'Connect Jira' },
  { pattern: /linear/i, icon: LinearIcon, label: 'Connect Linear' },
  {
    pattern: /google.?calendar/i,
    icon: GoogleCalendarIcon,
    label: 'Connect Google Calendar',
  },
  {
    pattern: /google.?sheets/i,
    icon: GoogleSheetsIcon,
    label: 'Connect Google Sheets',
  },
  {
    pattern: /google.?docs/i,
    icon: GoogleDocsIcon,
    label: 'Connect Google Docs',
  },
  {
    pattern: /google.?drive/i,
    icon: GoogleDriveIcon,
    label: 'Connect Google Drive',
  },
  { pattern: /notion/i, icon: NotionIcon, label: 'Connect Notion' },
  { pattern: /airtable/i, icon: AirtableIcon, label: 'Connect Airtable' },
  { pattern: /hubspot/i, icon: HubSpotIcon, label: 'Connect HubSpot' },
  { pattern: /twitter|(?:^|\b)x(?:\b|$)/i, icon: XIcon, label: 'Connect X' },
  { pattern: /supabase/i, icon: SupabaseIcon, label: 'Connect Supabase' },
];

// Matches intact and LLM-mangled variants (carbonarc / car-arc / cararc):
// agent-relayed hrefs arrive corrupted, so the click handler mints a fresh
// authorize URL server-side instead of trusting the href.
const CARBON_ARC_LINK = /car(?:bon)?[\s-]?arc/i;

function isCarbonArcConnectLink(href: string, text: string): boolean {
  return CARBON_ARC_LINK.test(href) || CARBON_ARC_LINK.test(text);
}

// An authorize card is a claim about what the link does, so the link has to
// say so itself: either in its words, or with a path that is an OAuth entry
// point. Without this, any link that merely happens to contain a provider's
// name became a connect card. `auth`/`connect` are deliberately left out of
// the path pattern: they're common enough as ordinary folder names (a repo
// tree, a product's own dashboard route) that path-only matching on them
// false-triggers on links with no OAuth intent at all -- `oauth`/`authorize`
// are specific enough to keep as a path-only signal.
const CONNECT_INTENT_TEXT = /\b(connect|authori[sz]e|sign in|log in)\b/i;
const CONNECT_INTENT_PATH = /(?:^|\/)(?:oauth\d?|authori[sz]e)(?:\/|$)/i;

function connectHrefParts(href: string): { host: string; pathname: string } {
  try {
    const url = new URL(href, window.location.origin);
    return { host: url.host, pathname: url.pathname };
  } catch {
    return { host: '', pathname: '' };
  }
}

function getConnectionInfo(href: string, text: string) {
  const { host, pathname } = connectHrefParts(href);
  if (!CONNECT_INTENT_TEXT.test(text) && !CONNECT_INTENT_PATH.test(pathname)) {
    return null;
  }

  // Host and path only, never the query: a presigned artifact URL carries
  // `X-Amz-Signature`, and that standalone `X` reads as the social network,
  // which turned a generated file into a "Connect X" card pointing at the
  // file.
  const haystack = `${text} ${host} ${pathname}`;
  for (const entry of CONNECTION_PATTERNS) {
    if (entry.pattern.test(haystack)) {
      return entry;
    }
  }
  // Generic connection card for any link with "connect" in the text
  if (/connect/i.test(text)) {
    return {
      icon: Link2,
      label: text,
    };
  }
  return null;
}

/**
 * Extracts just the workflow name from link text like "Open your Cat Slide workflow".
 * Falls back to the full text if no pattern matches.
 */
function extractWorkflowName(text: string): string {
  const match = text.match(/^(?:Open|View)\s+(?:your\s+)?(.+?)\s+workflow$/i);
  return match ? match[1] : text;
}

/**
 * The single source of truth for "does this href name a workflow to card,"
 * shared by the `a`/`p` renderers below and the tool-call blockquote path --
 * previously each re-derived this with its own substring check, and they
 * disagreed: an external link whose query string merely contained
 * `/workflow/...` got carded, and `/workflow/new` (the create-editor route,
 * not a saved workflow) was treated as one. Deliberately not restricted to
 * same-origin: agent-authored links are commonly absolute, built server-side
 * against a configured app URL that legitimately differs from whatever
 * origin this page happens to be served from (a preview/staging domain, a
 * local dev port) -- rejecting those would drop the card for the common
 * case, not just a rare foreign-domain coincidence.
 */
function workflowIdFromHref(href: string): string | null {
  try {
    const pathname = new URL(href, window.location.origin).pathname;
    const match = pathname.match(/\/workflow\/([^/?#]+)/);
    if (!match) return null;
    const id = decodeURIComponent(match[1]);
    return id === 'new' || id.startsWith('new:') ? null : id;
  } catch {
    return null;
  }
}

function MarkdownWorkflowCard({
  workflowId,
  linkText,
}: {
  workflowId: string;
  linkText: string;
}) {
  const { summary } = useWorkflowSummary(workflowId);
  const onOpen = useWorkflowCardOpen(workflowId);

  return (
    <div className='my-3'>
      <WorkflowCard
        testId='markdown-workflow-card'
        // The summary is a round trip away, and never arrives at all for a
        // workflow this viewer cannot read, so the link's own text carries the
        // card until then. Waiting for the summary instead leaves the agent's
        // handoff looking like a bare link.
        name={summary?.name || linkText || 'Workflow'}
        description={summary?.description}
        isPublic={summary?.isPublic}
        nodeCount={summary?.nodeCount}
        lastRunAt={summary?.lastRunAt}
        clientData={summary?.clientData}
        collaborators={summary?.collaborators}
        onOpen={onOpen}
      />
    </div>
  );
}

/** Plain text of a hast node, for classifying a link before it renders. */
function hastTextContent(node: unknown): string {
  const n = node as { type?: string; value?: string; children?: unknown[] };
  if (n?.type === 'text') return n.value ?? '';
  return (n?.children ?? []).map(hastTextContent).join('');
}

/**
 * True when a link renders as a card rather than as text. Mirrors the order
 * the `a` renderer classifies in, since a card is a <div> and cannot legally
 * nest inside the <p> a paragraph would otherwise get.
 */
function linkRendersAsCard(href: string, text: string): boolean {
  if (isCarbonArcConnectLink(href, text)) return true;
  if (!isNavigableHref(href)) return false;
  if (workflowIdFromHref(href)) return true;
  return !!getConnectionInfo(href, text);
}

/**
 * True when any link in this subtree renders as a card -- walks the whole
 * paragraph, not just its direct children, since a workflow link wrapped in
 * emphasis (`**[...](...)**`) is a grandchild, not a child.
 */
function hastHoldsCard(node: unknown): boolean {
  const n = node as {
    type?: string;
    tagName?: string;
    properties?: Record<string, unknown>;
    children?: unknown[];
  };
  if (
    n?.type === 'element' &&
    n.tagName === 'a' &&
    typeof n.properties?.href === 'string' &&
    linkRendersAsCard(n.properties.href, hastTextContent(n).trim())
  ) {
    return true;
  }
  return (n?.children ?? []).some(hastHoldsCard);
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg'];

/**
 * True when a link target points at an image file (by pathname extension,
 * so presigned URLs with long query strings still match).
 */
function isImageHref(href: string): boolean {
  try {
    const pathname = new URL(href, window.location.origin).pathname;
    const ext = pathname.split('.').pop()?.toLowerCase() ?? '';
    return IMAGE_EXTENSIONS.includes(ext);
  } catch {
    return false;
  }
}

// Hosts whose images are safe to auto-load: our own S3 asset buckets and
// platform domains. Anything else is refused so agent- or user-authored
// markdown can't beacon the viewer's IP/timing to an arbitrary server just
// by rendering.
const TRUSTED_IMAGE_HOST_SUFFIXES = ['.amazonaws.com', '.capitol.ai'];

function isTrustedImageHost(hostname: string): boolean {
  return TRUSTED_IMAGE_HOST_SUFFIXES.some(
    (suffix) => hostname === suffix.slice(1) || hostname.endsWith(suffix),
  );
}

/**
 * Resolves an image link to a source safe to auto-load, or null when it
 * isn't. Same-origin, proxy-rewritten backend files, inline (data:/blob:)
 * images, and our trusted asset hosts qualify; foreign hosts fall back to a
 * click-through link.
 */
function resolveInlineImageSrc(href: string): string | null {
  if (href.startsWith('data:') || href.startsWith('blob:')) return href;
  const proxied = toSameOriginWorkflowUrl(href);
  if (proxied.startsWith('/')) return proxied;
  try {
    const parsed = new URL(proxied, window.location.origin);
    if (
      parsed.origin === window.location.origin ||
      isTrustedImageHost(parsed.hostname)
    ) {
      return proxied;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Unfurls image links into an inline preview. Falls back to a plain
 * link when the image fails to load (expired presigned URL, auth wall).
 */
function UnfurledImageLink({
  href,
  src,
  linkText,
}: {
  href: string;
  src: string;
  linkText: string;
}) {
  // Keyed by src rather than a bare boolean: react-markdown keys nodes by
  // position, so this instance is reused when a message's content changes
  // under it. A bare flag would carry one image's failure onto the next.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = failedSrc === src;

  if (failed) {
    return (
      <a
        className='underline underline-offset-2 text-primary'
        target='_blank'
        rel='noopener noreferrer'
        href={href}
      >
        {linkText || href}
      </a>
    );
  }

  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className='my-3 block w-fit no-underline'
    >
      <img
        src={src}
        alt={linkText || 'image'}
        loading='lazy'
        onError={() => setFailedSrc(src)}
        className='max-h-96 max-w-full rounded-lg border border-border/60 shadow-sm'
      />
      {linkText && (
        <span className='mt-1 block text-xs text-muted-foreground'>
          {linkText}
        </span>
      )}
    </a>
  );
}

/**
 * Renders markdown with opinionated styles that match the app theme.
 * - Adds clear visual treatment for headings, lists, blockquotes
 * - Distinguishes inline and block code with monospace and background
 */
/** Recursively extract plain text from React children (handles nested elements). */
function getTextContent(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getTextContent).join('');
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode };
    return getTextContent(props.children);
  }
  return '';
}

const CodeBlock = ({
  className: _className,
  children,
  ...props
}: {
  className?: string;
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLElement>) => {
  const [copied, setCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current !== null) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const raw = getTextContent(children);
  const textContent = raw.replace(/\n$/, '');
  // react-markdown 10 no longer passes `inline`, so block-ness comes from
  // this element's real ancestry (see CodeFenceContext) rather than its
  // content -- a backtick span can itself contain a raw newline, so treating
  // any newline as "must be a block" split the surrounding paragraph for a
  // span like `` `line one\nline two` ``.
  const isFenced = useContext(CodeFenceContext);
  const isBlock = isFenced || /(?:^|\s)language-/.test(_className ?? '');

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(textContent).then(() => {
      setCopied(true);
      if (copyResetTimeoutRef.current !== null) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        copyResetTimeoutRef.current = null;
        setCopied(false);
      }, 2000);
    });
  }, [textContent]);

  const isMermaid = /language-mermaid/.test(_className ?? '');
  const isStreaming = useContext(MarkdownStreamingContext);
  if (isBlock && isMermaid) {
    return <MermaidDiagram code={textContent} isStreaming={isStreaming} />;
  }

  if (!isBlock) {
    return (
      <code
        className='rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]'
        {...props}
      >
        {children}
      </code>
    );
  }

  return (
    <div className='group/code relative my-4'>
      <pre className='overflow-x-auto rounded-md bg-muted p-4 pr-10'>
        <code className='font-mono text-sm' {...props}>
          {children}
        </code>
      </pre>
      <button
        onClick={handleCopy}
        className={`absolute right-2 top-2 flex items-center gap-1 rounded px-1.5 py-1 text-xs opacity-0 transition-all group-hover/code:opacity-100 ${
          copied
            ? 'text-green-600 dark:text-green-400'
            : 'text-muted-foreground hover:bg-background/80 hover:text-foreground'
        }`}
        title='Copy to clipboard'
      >
        {copied ? (
          <>
            <Check className='h-3.5 w-3.5' />
            Copied
          </>
        ) : (
          <Copy className='h-3.5 w-3.5' />
        )}
      </button>
    </div>
  );
};

// Defined once at module scope. react-markdown maps each node type to a
// component, and a fresh object here means fresh component *types* on
// every render, which React cannot reconcile -- it unmounts and remounts
// the whole subtree, so a MermaidDiagram never survives long enough to
// finish its render. The streaming flag reaches CodeBlock by context for
// this reason; the map itself has to stay stable too.
const MARKDOWN_COMPONENTS: Components = {
  h1: (props) => <h1 className='mt-6 text-2xl font-bold' {...props} />,
  h2: (props) => <h2 className='mt-5 text-xl font-semibold' {...props} />,
  h3: (props) => <h3 className='mt-4 text-lg font-semibold' {...props} />,
  p: ({ node, ...props }) => {
    // A card inside a <p> is invalid nesting, so a paragraph
    // holding one gives up its tag, wherever in the sentence (and
    // at whatever depth -- emphasis, strong) the link sits.
    const holdsCard = !!node && hastHoldsCard(node);
    return holdsCard ? (
      <div className='my-2' {...props} />
    ) : (
      <p className='my-2' {...props} />
    );
  },
  ul: (props) => <ul className='my-2 ml-6 list-disc space-y-1' {...props} />,
  ol: (props) => <ol className='my-2 ml-6 list-decimal space-y-1' {...props} />,
  li: (props) => <li className='my-0.5' {...props} />,
  table: (props) => (
    <div className='my-4 w-full overflow-x-auto rounded-lg border border-border'>
      <table className='w-full caption-bottom text-sm' {...props} />
    </div>
  ),
  thead: (props) => (
    <thead className='bg-muted/50 [&_tr]:border-b' {...props} />
  ),
  tr: (props) => <tr className='border-b last:border-0' {...props} />,
  th: (props) => (
    <th
      className='h-10 px-3 text-left align-middle font-medium text-muted-foreground'
      {...props}
    />
  ),
  td: (props) => <td className='px-3 py-2 align-middle' {...props} />,
  a: ({ href, children, ...props }) => {
    const linkText = getTextContent(children).trim();

    // Carbon Arc mints its authorize URL at click time and never
    // trusts the href, so it still works from a corrupted one --
    // classify it before the guard below can strip the anchor.
    const isCarbonArc = isCarbonArcConnectLink(href ?? '', linkText);

    // A href we cannot navigate to renders as plain text rather than
    // an anchor. The empty-string case is the important one:
    // react-markdown blanks a destination whose protocol it rejects,
    // and `<a href="" target="_blank">` reopens the CURRENT page in a
    // new tab, which reads to the user as a silent redirect.
    if (!isCarbonArc && !isNavigableHref(href)) {
      return <span {...props}>{children}</span>;
    }

    // Workflow links use the same summary card as the workflow list.
    const workflowId = href ? workflowIdFromHref(href) : null;
    if (workflowId) {
      return (
        <MarkdownWorkflowCard
          workflowId={workflowId}
          linkText={extractWorkflowName(linkText)}
        />
      );
    }

    // Image links → inline unfurled preview (same-origin only)
    const inlineImageSrc =
      href && isImageHref(href) ? resolveInlineImageSrc(href) : null;
    if (href && inlineImageSrc) {
      return (
        <UnfurledImageLink
          href={href}
          src={inlineImageSrc}
          linkText={linkText}
        />
      );
    }

    // Connection/auth links → connect card. Carbon Arc is checked
    // independently of getConnectionInfo: agent output can mangle the
    // markdown so badly (broken link text, corrupted href) that the
    // generic classifier misses it, and it must still become the
    // working card rather than a dead plain link. (Classified
    // above, before the navigable-href guard.)
    const connInfo = href ? getConnectionInfo(href, linkText) : null;
    if (isCarbonArc || connInfo) {
      // Carbon Arc mints its own URL from the provider slug: the
      // relayed href carries single-use state that has usually
      // expired. Everything else still follows the href it came with.
      return isCarbonArc ? (
        <CarbonArcConnectCard />
      ) : (
        <IntegrationConnectCard
          label={connInfo!.label}
          icon={connInfo!.icon}
          href={href}
        />
      );
    }

    return (
      <a
        className='underline underline-offset-2 text-primary'
        target='_blank'
        rel='noopener noreferrer'
        href={href}
        {...props}
      >
        {children}
      </a>
    );
  },
  blockquote: ({ children }) => {
    // Detect tool-call blockquotes (rendered by onToolCall / onToolResult)
    // Pattern: <p><strong>ToolName</strong> status text...</p>
    const toolInfo = extractToolCallInfo(children);
    if (toolInfo) {
      return (
        <div className='my-2 space-y-2'>
          <div className='flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2'>
            {toolInfo.status === 'running' && (
              <Loader2 className='size-4 shrink-0 animate-spin text-blue-500' />
            )}
            {toolInfo.status === 'completed' && (
              <CheckCircle2 className='size-4 shrink-0 text-green-500' />
            )}
            {toolInfo.status === 'failed' && (
              <XCircle className='size-4 shrink-0 text-red-500' />
            )}
            {toolInfo.status === 'unknown' && (
              <Circle className='size-4 shrink-0 text-muted-foreground' />
            )}
            <div className='min-w-0 flex-1'>
              <span className='text-sm font-medium'>{toolInfo.name}</span>
              {toolInfo.detail && (
                <span className='ml-1.5 text-sm text-muted-foreground'>
                  {toolInfo.detail}
                </span>
              )}
            </div>
          </div>
          {toolInfo.workflowLink && (
            <div>
              <span className='mb-2 block text-sm text-muted-foreground'>
                Here's your workflow
              </span>
              <MarkdownWorkflowCard
                workflowId={toolInfo.workflowLink.workflowId}
                linkText={extractWorkflowName(toolInfo.workflowLink.text)}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <blockquote className='my-4 border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground'>
        {children}
      </blockquote>
    );
  },
  hr: (props) => <hr className='my-6 border-border/60' {...props} />,
  img: (props) => <img className='my-4 max-w-full rounded-md' {...props} />,
  // Signals real ancestry to CodeBlock (see CodeFenceContext)
  // rather than rendering a <pre> of its own -- CodeBlock's block
  // branch already renders the styled <pre> itself.
  pre: ({ children }) => (
    <CodeFenceContext.Provider value={true}>
      {children}
    </CodeFenceContext.Provider>
  ),
  code: CodeBlock,
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  isStreaming = false,
}) => {
  if (!content) return null;

  return (
    <MarkdownStreamingContext.Provider value={isStreaming}>
      <div
        className={
          className ??
          'markdown-content space-y-3 text-sm leading-7 md:text-base whitespace-pre-line'
        }
      >
        <Markdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
          {content}
        </Markdown>
      </div>
    </MarkdownStreamingContext.Provider>
  );
};

export default MarkdownRenderer;
